import type { PositionView } from "../portfolio/usePortfolio";
import type { BondSchedule } from "../bond/useBondSchedule";

// Палитра для сегментов доната + bar-чартов. Берём из самой темы (--accent
// и --ink) плюс несколько спокойных контрастов. Циклится, если эмитентов
// больше, чем цветов.
export const PALETTE = [
  "#1d4e89", // accent navy
  "#1f7a4d", // green
  "#a4322c", // red
  "#7a4cd6", // purple
  "#b8860b", // gold
  "#2f7d3a", // dark green
  "#1f6feb", // blue
  "#6b7280", // gray
];

export type IssuerSlice = {
  name: string;
  rub: number;
  color: string;
  positions: number;
};

// Группируем позиции по эмитенту = первое слово SECNAME (или SHORTNAME, если
// SECNAME пуст). Серверный issuer-парсинг — отдельный пункт ROADMAP, пока
// клиентская эвристика.
export function groupByIssuer(positions: PositionView[]): IssuerSlice[] {
  const map = new Map<string, IssuerSlice>();
  for (const v of positions) {
    const name = issuerName(v);
    const rub = v.current_market_value_rub ?? v.cost_basis_rub;
    const cur = map.get(name);
    if (cur) {
      cur.rub += rub;
      cur.positions += 1;
    } else {
      map.set(name, { name, rub, color: "", positions: 1 });
    }
  }
  const list = [...map.values()].sort((a, b) => b.rub - a.rub);
  list.forEach((s, i) => (s.color = PALETTE[i % PALETTE.length]));
  return list;
}

function issuerName(v: PositionView): string {
  const src = v.bond_secname ?? v.bond_short_name ?? v.position.secid;
  return src.trim() || v.position.secid;
}

export type MaturityBucket = {
  label: string;
  rub: number;
};

const BUCKET_DEFS: { label: string; max: number }[] = [
  { label: "≤3 мес", max: 90 },
  { label: "3–6 мес", max: 180 },
  { label: "6–12 мес", max: 365 },
  { label: "1–2 года", max: 730 },
  { label: "2–3 года", max: 1095 },
  { label: "3+ года", max: Infinity },
];

export function groupByMaturityBucket(positions: PositionView[]): MaturityBucket[] {
  const buckets = BUCKET_DEFS.map((d) => ({ label: d.label, rub: 0 }));
  for (const v of positions) {
    const days = v.days_to_maturity;
    if (days == null || days < 0) continue;
    const rub = v.current_market_value_rub ?? v.cost_basis_rub;
    for (let i = 0; i < BUCKET_DEFS.length; i++) {
      if (days <= BUCKET_DEFS[i].max) {
        buckets[i].rub += rub;
        break;
      }
    }
  }
  return buckets;
}

export type MonthBar = {
  label: string; // «май», «июн», …
  ymKey: string; // YYYY-MM, для сортировки и стабильного key
  rub: number;
  // Часть `rub`, спроецированная по последнему известному купону
  // плавающей бумаги (эмитент ещё не объявил ставку). Нужна, чтобы UI
  // мог визуально отделить «факт» от «ожидается».
  projectedRub: number;
  hasRedemption: boolean;
};

// Прогноз cashflow на N месяцев вперёд. Аггрегирует все будущие купоны и
// амортизации/погашения по месяцам, помечает месяц «крупным» (hasRedemption),
// если в нём есть amortization-событие — это меняет стиль бара.
//
// schedules — Map secid → schedule. Если для какого-то secid'а пусто,
// просто пропускаем (cashflow будет неполный, но не упадёт).
export function monthlyForecast(
  positions: PositionView[],
  schedules: Map<string, BondSchedule>,
  monthsAhead: number,
  fxRates: Record<string, number>,
): MonthBar[] {
  const today = new Date();
  const startKey = monthKey(today);
  const months: MonthBar[] = [];
  const cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  for (let i = 0; i < monthsAhead; i++) {
    months.push({
      label: MONTH_LABELS[cursor.getMonth()],
      ymKey: monthKey(cursor),
      rub: 0,
      projectedRub: 0,
      hasRedemption: false,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  const idxByKey = new Map(months.map((m, i) => [m.ymKey, i]));

  for (const v of positions) {
    const sched = schedules.get(v.position.secid);
    if (!sched) continue;
    const qty = v.position.quantity;
    // Спот-курс ЦБ; фолбэк на buy-time fx, если /api/fx ещё не загружен.
    const fx =
      fxRates[v.currency] ??
      (v.cost_basis_rub > 0 && v.cost_basis !== 0 ? v.cost_basis_rub / v.cost_basis : 1);

    // Плавающий купон: эмитент объявляет ставку перед каждым периодом.
    // Будущие записи приходят с amount=null. Берём last-known как
    // проекцию, чтобы прогноз не обрывался — то же, что
    // project_floating_coupons делает на бэке для YTM.
    let lastKnownAmount: number | null = null;
    for (const c of sched.coupons) {
      const realAmount = c.amount;
      const amount = realAmount ?? lastKnownAmount;
      if (amount == null) continue;
      if (realAmount != null) lastKnownAmount = realAmount;
      const k = c.date.slice(0, 7);
      const idx = idxByKey.get(k);
      if (idx == null) continue;
      if (k < startKey) continue;
      const rub = amount * qty * fx;
      months[idx].rub += rub;
      if (realAmount == null) months[idx].projectedRub += rub;
    }
    for (const a of sched.amortizations) {
      if (a.amount == null) continue;
      const k = a.date.slice(0, 7);
      const idx = idxByKey.get(k);
      if (idx == null) continue;
      if (k < startKey) continue;
      months[idx].rub += a.amount * qty * fx;
      months[idx].hasRedemption = true;
    }
  }
  return months;
}

function monthKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

const MONTH_LABELS = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

// Средневзвешенные показатели — по market value (или cost basis) в RUB.
// Возвращает null, если суммарный вес = 0 или нет входов.
export function weightedAvg(
  positions: PositionView[],
  pick: (v: PositionView) => number | null,
): number | null {
  let num = 0;
  let den = 0;
  for (const v of positions) {
    const w = v.current_market_value_rub ?? v.cost_basis_rub;
    const x = pick(v);
    if (w <= 0 || x == null || !Number.isFinite(x)) continue;
    num += x * w;
    den += w;
  }
  return den > 0 ? num / den : null;
}
